import { useState, useEffect } from "react";
import toast from "../../../../utils/toast";
import {
  masterPaymentGatewayList,
  getPaymentGatewayList,
  createPaymentGateway,
  togglePayGatewayIsActive,
  togglePayGatewayIsIntent,
} from "../../../../services/admin/adminServices";

export default function GatewayList() {
  const [gatewayList, setGatewayList] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [masterGatewayTypes, setMasterGatewayTypes] = useState([]);
  const [isModalOpenAddGateway, setIsModalOpenAddGateway] = useState(false);
  const [isModalOpenEditGateway, setIsModalOpenEditGateway] = useState(false);
  const [selectedGateway, setSelectedGateway] = useState(null);
  const [addGatewayLoading, setAddGatewayLoading] = useState(false);
  const [editGatewayLoading, setEditGatewayLoading] = useState(false);

  const ALLOWED_SOURCE_OPTIONS = [
    { value: 1, label: "FromApp" },
    { value: 2, label: "FromWeb" },
    { value: 3, label: "FromBoth" },
  ];

  const AGENT_TYPE_OPTIONS = [
    { value: 1, label: "App" },
    { value: 2, label: "Web" },
    { value: 3, label: "Both" },
  ];

  const INITIAL_GATEWAY_FORM = {
    paymentGatewayTypeId: "",
    merchantID: "",
    merchantKey: "",
    envCode: "",
    allowedSource: 1,
    agentType: 1,
    updateInterval: 0,
    isActive: false,
    isIntentAllowed: false,
  };
  const [addGatewayForm, setAddGatewayForm] = useState(INITIAL_GATEWAY_FORM);
  const [editGatewayForm, setEditGatewayForm] = useState({});

  useEffect(() => {
    fetchGatewayList();
  }, []);

  useEffect(() => {
    if (isModalOpenAddGateway || isModalOpenEditGateway) {
      fetchMasterGatewayTypes();
    }
  }, [isModalOpenAddGateway, isModalOpenEditGateway]);

  const fetchGatewayList = async () => {
    setListLoading(true);
    try {
      const res = await getPaymentGatewayList();
      if (res.statusCode === 1) setGatewayList(res.data);
    } catch (err) {
      console.log("Failed to fetch payment gateways");
    } finally {
      setListLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const fetchMasterGatewayTypes = async () => {
    try {
      const res = await masterPaymentGatewayList();
      if (res.statusCode === 1) setMasterGatewayTypes(res.data);
    } catch (err) {
      console.log("Failed to fetch master gateway types");
    }
  };

  const openAddGatewayModal = () => {
    setAddGatewayForm(INITIAL_GATEWAY_FORM);
    setIsModalOpenAddGateway(true);
  };

  const closeAddGatewayModal = () => {
    setIsModalOpenAddGateway(false);
    setAddGatewayForm(INITIAL_GATEWAY_FORM);
  };

  const handleAddGatewayChange = (e) => {
    const { name, value, type, checked } = e.target;
    setAddGatewayForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleCreateGateway = async () => {
    setAddGatewayLoading(true);
    try {
      const payload = {
        id: 0,
        paymentGatewayTypeId: Number(addGatewayForm.paymentGatewayTypeId),
        merchantID: addGatewayForm.merchantID,
        merchantKey: addGatewayForm.merchantKey,
        envCode: addGatewayForm.envCode,
        allowedSource: Number(addGatewayForm.allowedSource),
        agentType: Number(addGatewayForm.agentType),
        updateInterval: Number(addGatewayForm.updateInterval),
        isActive: addGatewayForm.isActive,
        isIntentAllowed: addGatewayForm.isIntentAllowed,
        entryBy: 0,
        entryDate: new Date().toISOString(),
      };
      const res = await createPaymentGateway(payload);
      if (res.statusCode === 1) {
        toast.success(res.message);
        closeAddGatewayModal();
        fetchGatewayList();
      } else {
        toast.error(res.message);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Something went wrong.");
    } finally {
      setAddGatewayLoading(false);
    }
  };

  const openEditGatewayModal = (gw) => {
    setSelectedGateway(gw);
    setEditGatewayForm({
      paymentGatewayTypeId: gw.paymentGatewayTypeId || "",
      merchantID: gw.merchantID || "",
      merchantKey: gw.merchantKey || "",
      envCode: gw.envCode || "",
      allowedSource: gw.allowedSource || 1,
      agentType: gw.agentType || 1,
      updateInterval: gw.updateInterval || 0,
      isActive: gw.isActive,
      isIntentAllowed: gw.isIntentAllowed,
    });
    setIsModalOpenEditGateway(true);
  };

  const closeEditGatewayModal = () => {
    setIsModalOpenEditGateway(false);
    setSelectedGateway(null);
    setEditGatewayForm({});
  };

  const handleEditGatewayChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditGatewayForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleUpdateGateway = async () => {
    setEditGatewayLoading(true);
    try {
      const payload = {
        id: selectedGateway.id,
        paymentGatewayTypeId: Number(editGatewayForm.paymentGatewayTypeId),
        merchantID: editGatewayForm.merchantID,
        merchantKey: editGatewayForm.merchantKey,
        envCode: editGatewayForm.envCode,
        allowedSource: Number(editGatewayForm.allowedSource),
        agentType: Number(editGatewayForm.agentType),
        updateInterval: Number(editGatewayForm.updateInterval),
        isActive: editGatewayForm.isActive,
        isIntentAllowed: editGatewayForm.isIntentAllowed,
        entryBy: selectedGateway.entryBy || 0,
        entryDate: selectedGateway.entryDate,
      };
      const res = await createPaymentGateway(payload);
      if (res.statusCode === 1) {
        toast.success(res.message);
        closeEditGatewayModal();
        fetchGatewayList();
      } else {
        toast.error(res.message);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Something went wrong.");
    } finally {
      setEditGatewayLoading(false);
    }
  };

  const handleGatewayToggle = async (gw) => {
    const newValue = !gw.isActive;
    setGatewayList((prev) =>
      prev.map((g) => (g.id === gw.id ? { ...g, isActive: newValue } : g)),
    );
    try {
      const res = await togglePayGatewayIsActive(gw.id, newValue);
      if (res.statusCode === 1) {
        toast.success(res.message);
      } else {
        toast.error(res.message || "Failed to update.");
        setGatewayList((prev) => prev.map((g) => (g.id === gw.id ? gw : g)));
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Something went wrong.");
      setGatewayList((prev) => prev.map((g) => (g.id === gw.id ? gw : g)));
    }
  };

  const handleIntentToggle = async (gw) => {
    const newValue = !gw.isIntentAllowed;

    setGatewayList((prev) =>
      prev.map((g) =>
        g.id === gw.id ? { ...g, isIntentAllowed: newValue } : g,
      ),
    );

    try {
      const res = await togglePayGatewayIsIntent(gw.id, newValue);
      if (res.statusCode === 1) {
        toast.success(res.message);
      } else {
        toast.error(res.message || "Failed to update.");
        setGatewayList((prev) => prev.map((g) => (g.id === gw.id ? gw : g)));
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Something went wrong.");
      setGatewayList((prev) => prev.map((g) => (g.id === gw.id ? gw : g)));
    }
  };

  return (
    <>
      <div className="main">
        <div className="topbar">
          <div>
            <div className="page-title" id="page-title">
              Payment Gateway
            </div>
            <div className="page-sub" id="page-sub">
              System › Payment Gateway Configuration
            </div>
          </div>
          <div className="topbar-right" id="topbar-right">
            <button className="btn btn-primary" onClick={openAddGatewayModal}>
              + New Gateway
            </button>
          </div>
        </div>
        <div className="content" id="content-area">
          <div className="card" style={{ overflowX: "auto" }}>
            <table style={{ minWidth: 1200 }}>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Name</th>
                  <th>MerchantID</th>
                  <th>MerchantKey</th>
                  <th>ENVCode</th>
                  <th>AllowedSource</th>
                  <th>IndustryType</th>
                  <th>IsActive</th>
                  <th>IsIntentAllowed</th>
                  <th>EntryBy</th>
                  <th>EntryDate</th>
                  <th>ModifyBy</th>
                  <th>ModifyDate</th>
                  <th>AgentType</th>
                  <th>UpdateInMinutes</th>
                  <th style={{ minWidth: 80 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {listLoading ? (
                  Array.from({ length: 4 }).map((_, i) => (
                    <tr key={i}>
                      {Array.from({ length: 16 }).map((_, j) => (
                        <td key={j}>
                          <div
                            style={{
                              height: 14,
                              borderRadius: 6,
                              background:
                                "linear-gradient(90deg, var(--bg-secondary) 25%, var(--border-light) 50%, var(--bg-secondary) 75%)",
                              backgroundSize: "200% 100%",
                              animation: "shimmer 1.4s infinite",
                              width: "70%",
                            }}
                          />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : gatewayList.length === 0 ? (
                  <tr>
                    <td
                      colSpan={16}
                      style={{
                        textAlign: "center",
                        color: "var(--text-tertiary)",
                        padding: 24,
                      }}
                    >
                      No payment gateways found.
                    </td>
                  </tr>
                ) : (
                  gatewayList.map((gw, index) => (
                    <tr key={gw.id}>
                      <td className="sn-col">{index + 1}</td>
                      <td>
                        <span
                          style={{
                            color: "var(--blue)",
                            fontWeight: 600,
                            cursor: "pointer",
                          }}
                        >
                          {gw.paymentGatewayType || "—"}
                        </span>
                      </td>
                      <td
                        style={{
                          fontSize: 11,
                          fontFamily: "monospace",
                          maxWidth: 180,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                        title={gw.merchantID}
                      >
                        {gw.merchantID || "—"}
                      </td>
                      <td
                        style={{
                          fontSize: 11,
                          fontFamily: "monospace",
                          maxWidth: 160,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                        title={gw.merchantKey}
                      >
                        {gw.merchantKey || "—"}
                      </td>
                      <td>
                        <span className="badge badge-gray">
                          {gw.envCode || "—"}
                        </span>
                      </td>
                      <td>{gw.allowedSourceText || "—"}</td>
                      <td>—</td>
                      <td>
                        <label className="toggle">
                          <input
                            type="checkbox"
                            checked={gw.isActive}
                            onChange={() => handleGatewayToggle(gw)}
                          />
                        </label>
                        <div>
                          {gw.isActive ? (
                            <button
                              className="btn btn-xs btn-danger"
                              onClick={() => handleGatewayToggle(gw)}
                            >
                              ⏸ Deactivate
                            </button>
                          ) : (
                            <button
                              className="btn btn-xs btn-success"
                              onClick={() => handleGatewayToggle(gw)}
                            >
                              ▶ Activate
                            </button>
                          )}
                        </div>
                      </td>
                      <td>
                        <label className="toggle">
                          <input
                            type="checkbox"
                            checked={gw.isIntentAllowed}
                            onChange={() => handleIntentToggle(gw)}
                          />
                        </label>
                        <div>
                          {gw.isIntentAllowed ? (
                            <button
                              className="btn btn-xs btn-danger"
                              onClick={() => handleIntentToggle(gw)}
                            >
                              ⏸ Deactivate
                            </button>
                          ) : (
                            <button
                              className="btn btn-xs btn-success"
                              onClick={() => handleIntentToggle(gw)}
                            >
                              ▶ Activate
                            </button>
                          )}
                        </div>
                      </td>
                      <td>{gw.entryBy || "—"}</td>
                      <td style={{ fontSize: 11, whiteSpace: "nowrap" }}>
                        {formatDate(gw.entryDate)}
                      </td>
                      <td>—</td>
                      <td style={{ fontSize: 11, whiteSpace: "nowrap" }}>—</td>
                      <td>{gw.agentTypeText || "—"}</td>
                      <td>{gw.updateInterval ?? 0}</td>
                      <td>
                        <div className="td-actions">
                          <span
                            className="action-icon"
                            title="Edit"
                            onClick={() => openEditGatewayModal(gw)}
                          >
                            <span className="tip">Edit</span>✎
                          </span>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {isModalOpenAddGateway && (
          <div className="modal-overlay open">
            <div className="modal" style={{ width: 560 }}>
              <div className="modal-header">
                <div className="modal-title">New Payment Gateway</div>
                <button className="modal-close" onClick={closeAddGatewayModal}>
                  ✕
                </button>
              </div>
              <div className="modal-body">
                <div className="form-group mb12">
                  <label>Payment Gateway Type *</label>
                  <select
                    name="paymentGatewayTypeId"
                    value={addGatewayForm.paymentGatewayTypeId}
                    onChange={handleAddGatewayChange}
                  >
                    <option value="">Select Gateway Type</option>
                    {masterGatewayTypes.map((t) => (
                      <option
                        key={t.paymentGatewayTypeID}
                        value={t.paymentGatewayTypeID}
                      >
                        {t.paymentGatewayType}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group mb12">
                  <label>MerchantID</label>
                  <input
                    type="text"
                    name="merchantID"
                    value={addGatewayForm.merchantID}
                    onChange={handleAddGatewayChange}
                    placeholder="Enter Merchant ID from payment provider"
                  />
                </div>

                <div className="form-group mb12">
                  <label>MerchantKey</label>
                  <input
                    type="text"
                    name="merchantKey"
                    value={addGatewayForm.merchantKey}
                    onChange={handleAddGatewayChange}
                    placeholder="Enter Merchant Key / Secret Key"
                    style={{ background: "var(--bg-secondary)" }}
                  />
                </div>

                <div className="form-group mb12">
                  <label>ENVCode</label>
                  <input
                    type="text"
                    name="envCode"
                    value={addGatewayForm.envCode}
                    onChange={handleAddGatewayChange}
                    placeholder="e.g. WEBPROD, TestMode, Stag"
                  />
                </div>

                <div className="form-grid mb12">
                  <div className="form-group">
                    <label>AllowedSource</label>
                    <select
                      name="allowedSource"
                      value={addGatewayForm.allowedSource}
                      onChange={handleAddGatewayChange}
                    >
                      {ALLOWED_SOURCE_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>AgentType</label>
                    <select
                      name="agentType"
                      value={addGatewayForm.agentType}
                      onChange={handleAddGatewayChange}
                    >
                      {AGENT_TYPE_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-group mb12">
                  <label>Update Interval (Minutes)</label>
                  <input
                    type="number"
                    name="updateInterval"
                    value={addGatewayForm.updateInterval}
                    onChange={handleAddGatewayChange}
                    min={0}
                    placeholder="0"
                  />
                </div>

                <div style={{ display: "flex", gap: 24, marginTop: 6 }}>
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      fontSize: 13,
                      cursor: "pointer",
                    }}
                  >
                    <label className="toggle">
                      <input
                        type="checkbox"
                        name="isActive"
                        checked={addGatewayForm.isActive}
                        onChange={handleAddGatewayChange}
                      />
                      <span className="toggle-slider" />
                    </label>
                    IsActive
                  </label>
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      fontSize: 13,
                      cursor: "pointer",
                    }}
                  >
                    <label className="toggle">
                      <input
                        type="checkbox"
                        name="isIntentAllowed"
                        checked={addGatewayForm.isIntentAllowed}
                        onChange={handleAddGatewayChange}
                      />
                      <span className="toggle-slider" />
                    </label>
                    IsIntentAllowed
                  </label>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  className="btn"
                  onClick={closeAddGatewayModal}
                  disabled={addGatewayLoading}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleCreateGateway}
                  disabled={addGatewayLoading}
                >
                  {addGatewayLoading ? "Saving..." : "Save Gateway"}
                </button>
              </div>
            </div>
          </div>
        )}
        {isModalOpenEditGateway && (
          <div className="modal-overlay open">
            <div className="modal" style={{ width: 560 }}>
              <div className="modal-header">
                <div className="modal-title">
                  Edit Payment Gateway — {selectedGateway?.paymentGatewayType}
                </div>
                <button className="modal-close" onClick={closeEditGatewayModal}>
                  ✕
                </button>
              </div>
              <div className="modal-body">
                <div className="form-group mb12">
                  <label>Payment Gateway Type *</label>
                  <select
                    name="paymentGatewayTypeId"
                    value={editGatewayForm.paymentGatewayTypeId}
                    onChange={handleEditGatewayChange}
                  >
                    <option value="">Select Gateway Type</option>
                    {masterGatewayTypes.map((t) => (
                      <option
                        key={t.paymentGatewayTypeID}
                        value={t.paymentGatewayTypeID}
                      >
                        {t.paymentGatewayType}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group mb12">
                  <label>MerchantID</label>
                  <input
                    type="text"
                    name="merchantID"
                    value={editGatewayForm.merchantID}
                    onChange={handleEditGatewayChange}
                    placeholder="Enter Merchant ID from payment provider"
                  />
                </div>

                <div className="form-group mb12">
                  <label>MerchantKey</label>
                  <input
                    type="text"
                    name="merchantKey"
                    value={editGatewayForm.merchantKey}
                    onChange={handleEditGatewayChange}
                    placeholder="Enter Merchant Key / Secret Key"
                    style={{ background: "var(--bg-secondary)" }}
                  />
                </div>

                <div className="form-group mb12">
                  <label>ENVCode</label>
                  <input
                    type="text"
                    name="envCode"
                    value={editGatewayForm.envCode}
                    onChange={handleEditGatewayChange}
                    placeholder="e.g. WEBPROD, TestMode, Stag"
                  />
                </div>

                <div className="form-grid mb12">
                  <div className="form-group">
                    <label>AllowedSource</label>
                    <select
                      name="allowedSource"
                      value={editGatewayForm.allowedSource}
                      onChange={handleEditGatewayChange}
                    >
                      {ALLOWED_SOURCE_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>AgentType</label>
                    <select
                      name="agentType"
                      value={editGatewayForm.agentType}
                      onChange={handleEditGatewayChange}
                    >
                      {AGENT_TYPE_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-group mb12">
                  <label>Update Interval (Minutes)</label>
                  <input
                    type="number"
                    name="updateInterval"
                    value={editGatewayForm.updateInterval}
                    onChange={handleEditGatewayChange}
                    min={0}
                    placeholder="0"
                  />
                </div>

                <div style={{ display: "flex", gap: 24, marginTop: 6 }}>
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      fontSize: 13,
                      cursor: "pointer",
                    }}
                  >
                    <label className="toggle">
                      <input
                        type="checkbox"
                        name="isActive"
                        checked={editGatewayForm.isActive}
                        onChange={handleEditGatewayChange}
                      />
                      <span className="toggle-slider" />
                    </label>
                    IsActive
                  </label>
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      fontSize: 13,
                      cursor: "pointer",
                    }}
                  >
                    <label className="toggle">
                      <input
                        type="checkbox"
                        name="isIntentAllowed"
                        checked={editGatewayForm.isIntentAllowed}
                        onChange={handleEditGatewayChange}
                      />
                      <span className="toggle-slider" />
                    </label>
                    IsIntentAllowed
                  </label>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  className="btn"
                  onClick={closeEditGatewayModal}
                  disabled={editGatewayLoading}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleUpdateGateway}
                  disabled={editGatewayLoading}
                >
                  {editGatewayLoading ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
